import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import * as React from "react"

import { cn } from "@/lib/utils"

/** "Nadia Alvarez" → NA · "n.alvarez@pyxisrc.com" → NA · "Acme" → AC */
export function initialsOf(name: string): string {
  const clean = name.includes("@") ? (name.split("@")[0] ?? name) : name
  const parts = clean.split(/[\s._-]+/).filter(Boolean)
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0]! + parts[1][0]!).toUpperCase()
  }
  return clean.slice(0, 2).toUpperCase()
}

const SIZE = {
  xs: "size-5 text-[9px]",
  sm: "size-6 text-micro",
  md: "size-7 text-micro",
  lg: "size-9 text-caption",
} as const

export interface AvatarProps extends React.ComponentPropsWithoutRef<"span"> {
  name: string
  /** Stable identity for the colour. Falls back to `name`. */
  identity?: string | undefined
  size?: keyof typeof SIZE
  /** Photo, when there is one. Initials remain the fallback. */
  src?: string | undefined
  /** Ring in the surface colour, for overlapping stacks. */
  ringed?: boolean
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { className, name, identity: _identity, size = "md", src, ringed = false, ...props },
  ref,
) {
  const [failed, setFailed] = React.useState(false)

  return (
    <span
      ref={ref}
      title={name}
      className={cn(
        "relative grid shrink-0 place-items-center overflow-hidden rounded-full",
        "select-none border border-border bg-surface text-text font-id font-semibold",
        SIZE[size],
        className,
      )}
      {...props}
    >
      {src && !failed ? (
        <img
          src={src}
          alt=""
          onError={() => {
            setFailed(true)
          }}
          className="size-full object-cover"
        />
      ) : (
        <span aria-hidden="true">{initialsOf(name)}</span>
      )}
      <span className="sr-only">{name}</span>
    </span>
  )
})

export interface AvatarGroupProps {
  people: Array<{ name: string; identity?: string; src?: string }>
  /** Shown before the +N. Default 3. */
  max?: number
  size?: keyof typeof SIZE
  className?: string
}

export function AvatarGroup({ people, max = 3, size = "md", className }: AvatarGroupProps) {
  const shown = people.slice(0, max)
  const rest = people.slice(max)

  return (
    <span className={cn("flex items-center", className)}>
      {shown.map((p, i) => (
        <Avatar
          key={p.identity ?? p.name}
          name={p.name}
          identity={p.identity}
          src={p.src}
          size={size}
          style={{ marginLeft: i === 0 ? 0 : 2 }}
        />
      ))}
      {rest.length > 0 && (
        <span
          title={rest.map((p) => p.name).join(", ")}
          className={cn(
            "grid shrink-0 place-items-center rounded-full",
            "border border-border bg-surface-sunken",
            "font-id font-semibold text-text-2",
            SIZE[size],
          )}
          style={{ marginLeft: 2 }}
        >
          +{rest.length}
        </span>
      )}
    </span>
  )
}

/* Names the socket state honestly — "live", "reconnecting", "offline".
   A silent green dot while the connection is down risks losing a
   collaborator's changes. */

export type ConnectionState = "live" | "reconnecting" | "offline"

const CONN: Record<ConnectionState, { label: string; dot: string; text: string; title: string }> = {
  live: {
    label: "Live",
    dot: "bg-master shadow-[0_0_0_3px_var(--master-tint)]",
    text: "text-text-3",
    title: "Connected. Changes from other people appear as they happen.",
  },
  reconnecting: {
    label: "Reconnecting",
    dot: "bg-print animate-pulse motion-reduce:animate-none",
    text: "text-print",
    title:
      "The connection dropped and is being re-established. Your work is saved; other people's changes may be a moment behind.",
  },
  offline: {
    label: "Offline",
    dot: "bg-err",
    text: "text-err",
    title:
      "No connection. Your dispositions are queued locally and will send when it returns — but you are not seeing anyone else's.",
  },
}

export function LiveIndicator({
  state,
  className,
}: {
  state: ConnectionState
  className?: string
}) {
  const c = CONN[state]
  return (
    <span
      title={c.title}
      aria-live="polite"
      className={cn("inline-flex items-center gap-1.5 text-caption", c.text, className)}
    >
      <span aria-hidden="true" className={cn("size-1.5 rounded-full", c.dot)} />
      {c.label}
    </span>
  )
}

/* Anyone idle past the threshold dims rather than disappearing — a name
   that vanishes reads as "they left", when usually it means a laptop lid
   closed. */

export interface Presence {
  name: string
  identity?: string
  /** ms since their last frame. */
  idleFor?: number
  /** They are typing in a comment box. */
  typing?: boolean
}

export function PresenceStack({
  people,
  idleAfter = 60_000,
  className,
}: {
  people: Presence[]
  idleAfter?: number
  className?: string
}) {
  if (people.length === 0) return null

  const first = people[0]
  const label =
    people.length === 1 && first
      ? `${first.name} is also here`
      : `${String(people.length)} others are here: ${people.map((p) => p.name).join(", ")}`

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>
        <span className={cn("flex items-center", className)} aria-label={label}>
          {people.slice(0, 4).map((p, i) => {
            const idle = (p.idleFor ?? 0) > idleAfter
            return (
              <span
                key={p.identity ?? p.name}
                className="relative"
                style={{ marginLeft: i === 0 ? 0 : -8, zIndex: 4 - i }}
              >
                <Avatar
                  name={p.name}
                  identity={p.identity}
                  size="sm"
                  ringed
                  className={cn("transition-opacity duration-260 ease-out", idle && "opacity-45")}
                />
                {p.typing && (
                  <span
                    aria-hidden="true"
                    className="absolute -bottom-0.5 -right-0.5 size-2 rounded-full bg-master ring-2 ring-[color:var(--border)]"
                  />
                )}
              </span>
            )
          })}
        </span>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="bottom"
          sideOffset={6}
          className="z-toast rounded-md bg-text px-2.5 py-2 text-caption text-[color:var(--surface)] shadow-e3"
        >
          <ul className="flex flex-col gap-1">
            {people.map((p) => (
              <li key={p.identity ?? p.name} className="flex items-center gap-2">
                <span>{p.name}</span>
                {p.typing && <span className="opacity-60">typing…</span>}
                {(p.idleFor ?? 0) > idleAfter && <span className="opacity-60">idle</span>}
              </li>
            ))}
          </ul>
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
